import { ArrowUpRight, LockIcon, Copy, Check } from "lucide-react";
import { HoverCard } from "@/src/components/design-system/HoverCard/HoverCard";
import { CodeMirrorEditor } from "@/src/components/editor";
import type { Prisma } from "@langfuse/shared";
import { Button } from "@/src/components/ui/button";
import { Separator } from "@/src/components/ui/separator";
import { useEffect, useState } from "react";
import { generateSchemaExample } from "../lib/generateSchemaExample";
import { copyTextToClipboard } from "@/src/utils/clipboard";

type DatasetSchemaHoverCardProps = {
  schema: Prisma.JsonValue;
  schemaType: "input" | "expectedOutput";
  showLabel?: boolean;
};

export const DatasetSchemaHoverCard: React.FC<DatasetSchemaHoverCardProps> = ({
  schema,
  schemaType,
  showLabel = false,
}) => {
  const title =
    schemaType === "input" ? "Input Schema" : "Expected Output Schema";

  const schemaString = JSON.stringify(schema, null, 2);

  // Generate example object from schema
  const [exampleObject, setExampleObject] = useState("");
  useEffect(() => {
    let cancelled = false;
    generateSchemaExample(schema).then((result) => {
      if (!cancelled) setExampleObject(result);
    });
    return () => {
      cancelled = true;
    };
  }, [schema]);

  // State for copy button feedback
  const [copied, setCopied] = useState(false);

  const handleCopyExample = async () => {
    if (!exampleObject) return;

    await copyTextToClipboard(exampleObject);

    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <HoverCard
      openDelay={200}
      closeDelay={100}
      collisionPadding={20}
      content={
        <div className="max-h-[80vh] w-[400px] overflow-auto p-3">
          <p className="text-sm font-bold">{title}</p>
          <p className="text-muted-foreground pt-2 text-sm">
            Learn more about{" "}
            <a
              href="https://json-schema.org/learn/miscellaneous-examples"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground inline-flex items-center underline"
            >
              JSON Schema
              <ArrowUpRight className="icon-sm ml-0.5" />
            </a>
          </p>
          <div className="mt-2">
            <CodeMirrorEditor
              mode="json"
              value={schemaString}
              onChange={() => {}} // Read-only
              className="max-h-[250px] overflow-y-auto"
              editable={false}
            />
          </div>

          {exampleObject && (
            <>
              <Separator className="my-4" />
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold">Example Object</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopyExample}
                  className="h-7 px-2"
                >
                  {copied ? (
                    <Check className="icon-base text-icon-foreground" />
                  ) : (
                    <Copy className="icon-base text-icon-foreground" />
                  )}
                </Button>
              </div>
              <div className="mt-2">
                <CodeMirrorEditor
                  mode="json"
                  value={exampleObject}
                  className="max-h-[250px] overflow-y-auto"
                  editable={false}
                />
              </div>
            </>
          )}
        </div>
      }
    >
      {({ getTriggerProps }) => (
        <Button
          variant="ghost"
          className="text-muted-foreground hover:bg-accent hover:text-accent-foreground inline-flex items-center gap-1.5 rounded p-1 text-xs transition-colors"
          aria-label={`View ${title}`}
          size="sm"
          {...getTriggerProps()}
        >
          <LockIcon className="icon-base text-icon-foreground" />
          {showLabel && <span>Schema enforced</span>}
        </Button>
      )}
    </HoverCard>
  );
};
